import { EventClient } from '@tanstack/devtools-event-client';

export type RhinoDevContext = {
  id: string;
  type: string;
  context: object;
};

export type RhinoDevtoolsEventMap = {
  'context-update': RhinoDevContext;
  'context-remove': { id: string };
};

export class RhinoDevtoolsEventClient extends EventClient<RhinoDevtoolsEventMap> {
  #contexts = new Map<string, RhinoDevContext>();
  #snapshot: Record<string, RhinoDevContext> = {};
  #listeners = new Set<() => void>();

  // Mirrors the base client's enabled flag (which it keeps private) so callers
  // can skip reporting work when no devtool is listening — see useRhinoDevtoolsEmit.
  readonly enabled: boolean;

  constructor(options: { enabled?: boolean } = {}) {
    const enabled = options.enabled ?? process.env.NODE_ENV === 'development';
    super({ pluginId: 'rhino', enabled });
    this.enabled = enabled;
  }

  // Emit on the TanStack bus (native event stream) AND update the local
  // registry that the panel renders from. The registry is the panel's data
  // source so rendering is robust to lazy plugin mounting and never depends
  // on the bus round-trip.
  reportContext(context: RhinoDevContext) {
    this.emit('context-update', context);
    this.#contexts.set(context.id, context);
    this.#notify();
  }

  clearContext(id: string) {
    this.emit('context-remove', { id });
    this.#contexts.delete(id);
    this.#notify();
  }

  // Stable, bound references for useSyncExternalStore.
  getSnapshot = (): Record<string, RhinoDevContext> => this.#snapshot;

  subscribe = (listener: () => void): (() => void) => {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  };

  #notify() {
    this.#snapshot = Object.fromEntries(this.#contexts);
    this.#listeners.forEach((listener) => listener());
  }
}

export const defaultDevtoolsEventClient = new RhinoDevtoolsEventClient();
